/**
 * The selection family's surface, as one vocabulary.
 *
 * Every constant carries the `_CLASSES` suffix because that is what the class
 * law (U19) matches: a module-scope string reaching the DOM through a `cn` in
 * another file is an identifier at the call site, and an unsuffixed name would
 * put the whole family outside the U7/W3 rules meant to govern it.
 *
 * Nothing here needs an RTL branch. The list is vertical, `align="start"` on the
 * popper is already logical, and every class below is either logical
 * (`ps-`/`text-start`) or axis-neutral. There is no direction handling to add.
 */

/**
 * The panel, as a flex CHILD of its popper.
 *
 * `min-h-0 flex-1` is load-bearing and was the defect the P10.2a walk measured:
 * the panel used to size itself (`w-… flex-col overflow-hidden`) while
 * `PopoverContent` clamped to `max-h-popover-available`. A panel taller than
 * that clamp then had 12px of itself cut off — silently, because the clamp is on
 * the popper and the only scroller is two levels down inside the panel.
 *
 * The popper is therefore the flex COLUMN (`flex flex-col overflow-hidden`, plus
 * the width) and the panel is the item that shrinks into it. Then the clamp
 * reaches the list, and the list scrolls.
 */
export const SELECTION_PANEL_CLASSES = 'flex min-h-0 w-full flex-1 flex-col';

/** What every selection popper's `PopoverContent` composes. */
export const SELECTION_POPPER_CLASSES = 'flex w-popover-trigger flex-col overflow-hidden p-0';

export const SELECTION_FILTER_ROW_CLASSES = 'shrink-0 border-b border-border p-md';

export const SELECTION_HEADER_ROW_CLASSES =
  'flex shrink-0 items-center gap-sm border-b border-border px-md py-sm';

/**
 * The option list. `p-sm` rather than `p-md`: at the render cap a list is a
 * hundred rows, and the wider padding made it mostly padding.
 */
export const SELECTION_LIST_CLASSES = 'flex min-h-0 flex-1 flex-col gap-xs p-sm';

export const SELECTION_GROUP_HEADING_CLASSES =
  'px-md pb-xs pt-sm text-caption font-medium text-muted-foreground';

/**
 * One option row. The row paints the hover and focus treatment for the control
 * inside it (`has-[:focus-visible]`), so the whole row reads as one target
 * instead of a 16px checkbox with text beside it.
 */
export const SELECTION_ROW_CLASSES =
  'flex items-start gap-sm rounded-sm px-md py-sm text-label transition-colors duration-fast ease-standard hover:bg-secondary has-[:focus-visible]:bg-secondary has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring aria-disabled:opacity-50';

/**
 * `items-start` is not decoration and must not be dropped: `Label`'s own base is
 * `inline-flex items-center`, and `flex-col` changes the AXIS without clearing
 * the alignment — so `items-center` survives and centres the label and its
 * description horizontally in the row. Every option in all three controls sat
 * ~230px adrift of its checkbox until this was added; no containment or width
 * measurement can see it, which is why it also carries a class-level spec.
 */
export const SELECTION_ROW_LABEL_CLASSES =
  'flex min-w-0 flex-1 cursor-pointer flex-col items-start gap-xs font-normal';

export const SELECTION_ROW_DESCRIPTION_CLASSES = 'text-caption font-normal text-muted-foreground';

/**
 * The row's control. `mt-2xs` is 2px from the preset's own scale — the optical
 * nudge that lines a 16px checkbox up with the first line of the 20px label
 * beside it, now that the row aligns to `items-start`.
 */
export const SELECTION_ROW_CONTROL_CLASSES = 'mt-2xs shrink-0';

export const SELECTION_FOOTER_CLASSES =
  'flex shrink-0 flex-col gap-xs border-t border-border px-md py-sm text-caption text-muted-foreground';

/** A state line inside the list — empty, loading, error, "type to narrow". */
export const SELECTION_NOTICE_CLASSES =
  'px-md py-md text-center text-caption text-muted-foreground';

export const SELECTION_CHIP_RAIL_CLASSES = 'flex flex-wrap items-center gap-sm';

/**
 * `min-w-0` rather than a width cap (W3): the chip is a flex item, and telling
 * it that it MAY shrink is what lets the label inside truncate. A `max-w-*`
 * would cap every chip at the rail's width whether it needed it or not.
 */
export const SELECTION_CHIP_CLASSES =
  'inline-flex min-w-0 items-center gap-sm rounded-full bg-secondary px-md py-xs text-caption text-secondary-foreground';

/* ── ToggleGroup ────────────────────────────────────────────────────────── */

/**
 * The switch group's own box.
 *
 * A border and padding rather than bare rows, because the group is ONE field
 * value: without an edge, four switches under a caption read as four fields that
 * happen to be adjacent.
 */
export const TOGGLE_GROUP_CLASSES = 'flex flex-col gap-sm rounded-md border border-border p-md';

export const TOGGLE_GROUP_ROW_CLASSES = 'flex items-start justify-between gap-md';

export const TOGGLE_GROUP_ROW_TEXT_CLASSES = 'flex min-w-0 flex-col gap-2xs';

export const TOGGLE_GROUP_LABEL_CLASSES = 'cursor-pointer font-normal';

export const TOGGLE_GROUP_DESCRIPTION_CLASSES = 'text-caption text-muted-foreground';

export const SELECTION_CHIP_REMOVE_CLASSES =
  'shrink-0 rounded-full text-muted-foreground transition-colors duration-fast ease-standard hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/* ── CountrySetField ────────────────────────────────────────────────────── */

/**
 * The remove button on a country chip: a 24px hit area rather than the bare
 * 12px glyph `SELECTION_CHIP_REMOVE_CLASSES` offers — a policy chip is removed
 * deliberately, and a target nobody can hit is pressed twice. `-me-sm` pulls
 * the button back into the chip's own end padding.
 */
export const COUNTRY_SET_CHIP_REMOVE_CLASSES =
  '-me-sm flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors duration-fast ease-standard hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** The "any country" state: a bordered note where the rail would be, so the emptiness is a value, not a gap. */
export const COUNTRY_SET_ANY_CLASSES =
  'flex items-start gap-sm rounded-md border border-dashed border-border px-md py-sm text-caption text-muted-foreground';

/* ── ChoiceCards ────────────────────────────────────────────────────────── */

/**
 * The card stack. ALWAYS one column, like every other list in this family
 * (`ToggleGroup`, `SelectionPanel`) — never a viewport-keyed `sm:grid-cols-3`.
 * This project has no container-query plugin, so a breakpoint here reads the
 * VIEWPORT, not the space `ChoiceCards` actually has: nested inside a form
 * column that shrinks when the editor's choices rail is open, three columns
 * fired at 1440px width squeezed each card to its longest word (found in the
 * P10.6e1 stills — the exact "seven columns, one word per line" defect
 * P10.6c's own stills first caught, from a different cause with the same
 * shape: something sized itself off the viewport instead of its container).
 * `RadioGroup`'s own base already supplies `grid gap-sm`.
 */
export const CHOICE_CARDS_GROUP_CLASSES = 'grid-cols-1';

/**
 * One card. `min-w-0`: a grid item's default `min-width` is `auto`, which for
 * text content is its longest word — without this a card refuses to shrink
 * below that and fights its siblings for space instead of wrapping.
 * `has-[[data-state=checked]]` reads the nested `RadioGroupItem`'s OWN state
 * rather than a class threaded down from the row — Radix already puts
 * `data-state` on the control, so the card reacts to it natively instead of a
 * second source of truth the two could drift apart from.
 */
export const CHOICE_CARDS_CARD_CLASSES =
  'flex min-w-0 cursor-pointer items-start gap-sm rounded-md border border-border p-sm transition-colors duration-fast ease-standard hover:bg-secondary has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-secondary/60 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50';

export const CHOICE_CARDS_CARD_TEXT_CLASSES = 'flex min-w-0 flex-col gap-2xs';

export const CHOICE_CARDS_DESCRIPTION_CLASSES = 'text-caption text-muted-foreground';
